import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Clock, Gauge, RefreshCw, Timer, TrendingUp, Wind } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import {
  computeFilterLifeDisplay,
  replacementStatusFromAlerts,
  FilterLifeDisplay,
} from '../utils/filterLifeDisplay';
import { Device } from '../types';
import { formatCalendarDate } from '../utils/calendarDate';
import Button from './ui/Button';

/**
 * FS-529 — App Graphs tab. Shows exactly what the MyFluxHive app displays for
 * this device: the stats card above the server-rendered filter-loading chart,
 * fetched on demand from GET /devices/:id/app-chart (device-data-api), which
 * reaches the same renderer the app calls. Works for any device in scope
 * regardless of app-side per-user enablement — the point is vetting the view
 * BEFORE enabling a user.
 *
 * ONE request serves both halves: the PNG response carries its own stats block
 * in X-Chart-Stats (FS-533), so the card and the image always describe the same
 * renderer snapshot. Fetching them separately let a reprocess landing between
 * the two requests produce a card and an image in different states.
 *
 * Layout, labels, and value formatting deliberately mirror the app's
 * DeviceChartScreen/ChartStatsCard (filter-app), including hiding the stats
 * card entirely on non-ok states — the server then serves a full-size
 * placeholder card as the image itself.
 */

/** The renderer's ?format=stats payload (render-analysis-chart stats.go). */
export interface AppChartStats {
  state: 'ok' | 'no_data' | 'calibrating' | 'not_reporting' | string;
  latest_processed_date?: string;
  window_start?: string;
  window_end?: string;
  window_anchored?: boolean;
  /** FS-607: which date the window is measured from. Absent on responses
   *  rendered before that change shipped. */
  anchor_kind?: 'filter_change' | 'install';
  days_since_change?: number | null;
  current_pressure_pa?: number | null;
  current_pressure_date?: string;
  daily_change_pa?: number | null;
  total_runtime_minutes?: number | null;
  avg_daily_runtime_minutes?: number | null;
  avg_daily_runtime_days?: number;
}

/** App date format ('MMMM d, y' → "August 17, 2026"); raw string on parse
 *  failure, exactly like the app's _fmtDate. Parsed by parts — Date() on a
 *  date-only string is UTC midnight and shifts a day in US timezones.
 *
 *  FS-488 moved the parsing into utils/calendarDate so the rest of the console
 *  reads its date-only values the same way. The name stays because this panel
 *  exists to mirror the app's own formatting. */
export const formatAppDate = formatCalendarDate;

/** The app's Filter Life value strings (chart_stats_card.dart _lifeText). */
export const filterLifeText = (d: FilterLifeDisplay): string => {
  switch (d.kind) {
    case 'expect_reading':
      return 'Expect reading in 24-48 hrs';
    case 'change_now':
      return 'Change now';
    case 'change_by':
      return `Change by ${formatAppDate(d.date)}`;
    case 'change_soon':
      return 'Change soon';
    case 'life_pct':
      return `${d.percent}%`;
  }
};

const TEAL = '#2AA1AF'; // the app's accent (CustomAppBar / icons)

const StatRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-2.5 pt-3">
    <span className="shrink-0" style={{ color: TEAL }}>{icon}</span>
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[15px] font-semibold text-gray-900">{value}</div>
    </div>
  </div>
);

const AppStatsCard: React.FC<{ stats: AppChartStats; device: Device }> = ({
  stats,
  device,
}) => {
  const { enrichmentMap } = useFirestoreEnrichment();
  const enrichment = enrichmentMap[device.name];

  // Same inputs the app's filter card passes down (FS-432): Firestore floor,
  // filter thickness, predicted change date. Never the raw pipeline %.
  const rawThickness = enrichment?.filter?.thickness;
  const thickness = rawThickness != null && !Number.isNaN(Number(rawThickness))
    ? Number(rawThickness)
    : null;
  // The alert-derived replacement status is authoritative over the floor in the
  // app: an open filter_needs_replacement alert makes the app say "Change now"
  // / "Change soon" while the floor still reads healthy. Without it this card
  // would show a percentage where the phone says change the filter (FS-432).
  const life = computeFilterLifeDisplay(
    enrichment?.device?.airflowPercentageFloor,
    thickness,
    enrichment?.filter?.predictedChangeDate,
    replacementStatusFromAlerts(enrichment?.filterAlerts),
  );

  // FS-607: anchor_kind names the date the count is measured from. The
  // renderer falls back to the install date whenever the filter baseline is
  // null, which is the case after every filter change until the nightly
  // catches up; calling that a filter change misreports the filter's age.
  // Older responses carry no anchor_kind — treat those as the install date
  // rather than asserting a change we cannot confirm.
  const header = stats.window_anchored && stats.days_since_change != null
    ? stats.anchor_kind === 'filter_change'
      ? `Since filter was changed — ${stats.days_since_change} days`
      : `Since installation — ${stats.days_since_change} days`
    : 'All available history';

  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="text-[13px] italic text-foreground">{header}</div>

      {stats.current_pressure_pa != null && stats.current_pressure_date && (
        <StatRow
          icon={<Gauge className="h-5 w-5" />}
          label={`Current Pressure (${formatAppDate(stats.current_pressure_date)})`}
          value={`${stats.current_pressure_pa.toFixed(2)} Pa`}
        />
      )}
      {stats.daily_change_pa != null && (
        <StatRow
          icon={<TrendingUp className="h-5 w-5" />}
          label="Daily Change (last 30d trend)"
          value={`${stats.daily_change_pa.toFixed(3)} Pa/day`}
        />
      )}
      {stats.total_runtime_minutes != null && stats.window_start && stats.window_end && (
        <StatRow
          icon={<Timer className="h-5 w-5" />}
          label={`Total Runtime (${formatAppDate(stats.window_start)} – ${formatAppDate(stats.window_end)})`}
          value={`${(stats.total_runtime_minutes / 60).toFixed(1)} hrs`}
        />
      )}
      {stats.avg_daily_runtime_minutes != null && !!stats.avg_daily_runtime_days && (
        <StatRow
          icon={<Clock className="h-5 w-5" />}
          label={`Average Daily Runtime (last ${stats.avg_daily_runtime_days}d)`}
          value={`${(stats.avg_daily_runtime_minutes / 60).toFixed(1)} hrs/day`}
        />
      )}
      <StatRow
        icon={<Wind className="h-5 w-5" />}
        label="Filter Life"
        value={filterLifeText(life)}
      />
    </div>
  );
};

/** With responseType 'blob' an error body arrives as a Blob, so axios never
 *  sees the API's `{ "error": ... }` text. Read it back before falling through
 *  to the generic extractor — an operator needs the explicit backend reason
 *  ("Chart renderer unavailable"), not a bare "502 Bad Gateway". */
export const chartErrorMessage = async (err: unknown): Promise<string> => {
  const response = axios.isAxiosError(err) ? err.response : undefined;
  if (response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await response.data.text());
      const detail = parsed?.error ?? parsed?.message;
      if (typeof detail === 'string' && detail) {
        return response.status ? `${response.status} ${detail}` : detail;
      }
    } catch {
      // Not JSON (or unreadable) — the generic extractor still has the status.
    }
  }
  return extractErrorMessage(err, 'Could not load the app chart preview');
};

interface DeviceAppGraphsPanelProps {
  device: Device;
}

const DeviceAppGraphsPanel: React.FC<DeviceAppGraphsPanelProps> = ({ device }) => {
  const [stats, setStats] = useState<AppChartStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageAlt, setImageAlt] = useState('Filter loading chart');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setStatsError(null);
    const url = `${config.devicesApiUrl}/devices/${device.id}/app-chart`;
    try {
      const res = await axiosInstance.get<Blob>(url, { responseType: 'blob' });

      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = URL.createObjectURL(res.data);
      setImageUrl(objectUrlRef.current);

      const summary = res.headers?.['x-chart-summary'];
      setImageAlt(typeof summary === 'string' && summary ? summary : 'Filter loading chart');

      // Same snapshot as the PNG above, by construction.
      const rawStats = res.headers?.['x-chart-stats'];
      if (typeof rawStats !== 'string' || !rawStats) {
        setStats(null);
        setStatsError('No X-Chart-Stats header on the chart response — showing the image only.');
      } else {
        try {
          setStats(JSON.parse(rawStats) as AppChartStats);
        } catch (parseErr) {
          setStats(null);
          setStatsError(`Could not parse X-Chart-Stats: ${(parseErr as Error).message}`);
        }
      }
    } catch (err) {
      console.error('Failed to load app chart preview:', err);
      setStats(null);
      setError(await chartErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [device.id]);

  useEffect(() => {
    load();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [load]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-800">App Graphs</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Exactly what the MyFluxHive app shows for this device, rendered on
            demand by the same service the app calls. Visible here even when the
            device&apos;s user doesn&apos;t have charts enabled yet.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={load}
          disabled={loading}
          className="shrink-0"
          title="Render the app's chart for this device again, from the latest data"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading chart…</p>
        ) : error ? (
          <div className="text-sm">
            <p role="alert" className="text-status-error-foreground">{error}</p>
            <Button
              variant="secondary"
              onClick={load}
              className="mt-2"
              title="Ask the chart service for this device's chart again"
            >
              Try again
            </Button>
          </div>
        ) : (
          /* Phone mirror: the app lays this out in a 12px-padded column on a
             white screen; the PNG is a 3x render of a ~400-logical-pt design,
             so cap the column at 400px to match what a phone shows. */
          <div className="max-w-[400px] rounded-lg border border-border bg-white p-3">
            {statsError && <p role="alert" className="pb-3 text-sm text-status-error-foreground">{statsError}</p>}
            {stats?.state === 'no_data' && <p role="status" className="pb-3 text-sm text-muted-foreground">No chart data is available for this device yet.</p>}
            {stats?.state === 'ok' && (
              <div className="pb-3">
                <AppStatsCard stats={stats} device={device} />
              </div>
            )}
            {imageUrl && (
              <img
                src={imageUrl}
                alt={imageAlt}
                className="w-full h-auto"
                style={{ aspectRatio: '4 / 3' }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceAppGraphsPanel;
